import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  forwardRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkConnectedOverlay, OverlayModule } from '@angular/cdk/overlay';
import { IdDescriptionPair } from '../../../core/models/static-data/static-data';

interface TimezoneGroup {
  region: string;
  zones: IdDescriptionPair[];
}

@Component({
  selector: 'app-searchable-timezone-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayModule, CdkConnectedOverlay],
  templateUrl: './searchable-timezone-dropdown.component.html',
  styleUrl: './searchable-timezone-dropdown.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableTimezoneDropdownComponent),
      multi: true
    }
  ]
})
export class SearchableTimezoneDropdownComponent implements ControlValueAccessor {
  @Input() zones: IdDescriptionPair[] = [];
  @Input() placeholder = 'Select a time zone';
  @Input() hasError = false;

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;

  @Output() selectionChange = new EventEmitter<any>();

  isOpen = false;
  searchText = '';
  selectedValue: any = null;
  triggerWidth: string | number = 'auto';

  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};

  get selectedLabel(): string {
    if (this.selectedValue === null || this.selectedValue === undefined || this.selectedValue === '') {
      return '';
    }
    const found = this.zones.find(z => z.id === Number(this.selectedValue));
    return found ? found.description : '';
  }

  get groupedFilteredZones(): TimezoneGroup[] {
    const term = this.searchText.toLowerCase().trim();
    const filtered = term
      ? this.zones.filter(z => z.description.toLowerCase().includes(term))
      : [...this.zones];

    const groupMap = new Map<string, IdDescriptionPair[]>();
    for (const zone of filtered) {
      const slashIdx = zone.description.indexOf('/');
      const region = slashIdx > -1 ? zone.description.substring(0, slashIdx).trim() : 'Other';
      if (!groupMap.has(region)) groupMap.set(region, []);
      groupMap.get(region)!.push(zone);
    }

    return Array.from(groupMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([region, zones]) => ({ region, zones }));
  }

  get hasResults(): boolean {
    return this.groupedFilteredZones.some(g => g.zones.length > 0);
  }

  getCityLabel(description: string): string {
    const slashIdx = description.indexOf('/');
    return slashIdx > -1 ? description.substring(slashIdx + 1).trim() : description;
  }

  open(triggerEl: HTMLElement): void {
    try {
      const rect = triggerEl.getBoundingClientRect();
      this.triggerWidth = rect.width > 0 ? rect.width : 'auto';
    } catch {
      this.triggerWidth = 'auto';
    }
    this.searchText = '';
    this.isOpen = true;
    setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 60);
  }

  close(): void {
    this.isOpen = false;
    this.searchText = '';
    this.onTouched();
  }

  toggle(triggerEl: HTMLElement): void {
    this.isOpen ? this.close() : this.open(triggerEl);
  }

  select(zone: IdDescriptionPair): void {
    this.selectedValue = zone.id;
    this.onChange(zone.id);
    this.selectionChange.emit(zone.id);
    this.onTouched();
    this.close();
  }

  // ControlValueAccessor
  writeValue(val: any): void {
    this.selectedValue = val;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {}
}
